import React, { useEffect, useRef, useCallback } from "react";
import { PageLayout } from "../components/page-layout";
import { sampleDocuments } from "../data/sample-documents";
import "../styles/components/home/index.css";
import "../styles/components/freebies-page-override.css";
import "../styles/components/gymea-edu-dashboard.css";

export const FreebieDownloadablesPage: React.FC = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const finalCtaRef = useRef<HTMLElement>(null);
  
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        if (entry.target.classList.contains('section-header')) {
          entry.target.classList.add("animate-underline");
        } else {
          entry.target.classList.add("animate-in");
        }
      }
    });
  }, []);

  useEffect(() => {
    const titleElement = titleRef.current;
    const finalCtaElement = finalCtaRef.current;
    
    if (!titleElement && !finalCtaElement) return;

    const observer = new IntersectionObserver(handleIntersection, { threshold: 0.1 });

    if (titleElement) observer.observe(titleElement);
    if (finalCtaElement) observer.observe(finalCtaElement);

    return () => {
      if (titleElement) observer.unobserve(titleElement);
      if (finalCtaElement) observer.unobserve(finalCtaElement);
      observer.disconnect();
    };
  }, [handleIntersection]);


  const categories = Array.from(new Set(sampleDocuments.map(doc => doc.category)));

  return (
    <PageLayout fullWidth={true} className="dashboard-layout">
      <div className="page-container dashboard-container">
        {/* Dashboard-style Header */}
        <div className="dashboard-header">
          <h1 ref={titleRef}>Freebie downloadables</h1>
          <div className="section-header">
            <h2>See what your custom creations will look like</h2>
          </div>
          <p className="dashboard-subheading">
            
          </p>
        </div>

        {/* Sample Downloads Section */}
        <section className="document-preview-section">
          <div className="document-preview-content">
            {categories.map(category => (
              <div key={category} className="sample-category-section">
                <div className="section-header">
                  <h2>{category}</h2>
                </div>
                
                <div className="document-tiles-grid">
                  {sampleDocuments
                    .filter(doc => doc.category === category)
                    .map(sampleDoc => {
                      const getBadgeClass = (documentType: string) => {
                        switch(documentType) {
                          case 'Lesson Plan': return 'most-used';
                          case 'Unit of Work': return 'popular';
                          case 'Assessment': return 'new';
                          case 'Template': return '';
                          default: return '';
                        }
                      };

                      return (
                        <div key={sampleDoc.id} className={`document-tile ${getBadgeClass(sampleDoc.documentType)} ${category === 'Individual Plans' ? 'individual-plans-tile' : ''}`}>
                          {sampleDoc.documentType !== 'Template' && (
                            <div className="tile-badge">{sampleDoc.documentType.toUpperCase()}</div>
                          )}
                          <div className="tile-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                          </div>
                          <h3>{sampleDoc.title}</h3>
                          <p>{sampleDoc.description}</p>
                          <div className="tile-features">
                            {sampleDoc.documentType !== 'Template' && sampleDoc.documentType !== 'Sample Plan' && (
                              <div className="document-details">
                                <div><strong>Year level:</strong> {sampleDoc.yearLevel}</div>
                                <div><strong>Curriculum:</strong> {sampleDoc.curriculum}</div>
                                <div><strong>Subject:</strong> {sampleDoc.subject}</div>
                                <div><strong>Topic:</strong> {sampleDoc.topic}</div>
                              </div>
                            )}
                          </div>
                          <div className="download-buttons">
                            <a
                              className="download-button"
                              href={sampleDoc.downloadUrl}
                              download
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                              </svg>
                              {sampleDoc.documentType === "Lesson Plan" ? "Download Lesson Plan" :
                               sampleDoc.documentType === "Unit of Work" ? "Download Unit of Work" : "Download Sample"}
                            </a>
                            {sampleDoc.slideDownloadUrl && (
                              <a
                                className="download-button download-button-secondary"
                                href={sampleDoc.slideDownloadUrl}
                                download
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7 10 12 15 17 10"></polyline>
                                  <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Download Slides
                              </a>
                            )}
                            {sampleDoc.worksheetDownloadUrl && (
                              <a
                                className="download-button download-button-secondary"
                                href={sampleDoc.worksheetDownloadUrl}
                                download
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7 10 12 15 17 10"></polyline>
                                  <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Download Worksheet
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}

          </div>
        </section>

        {/* CTA Section - Full Width */}
        <section className="final-cta-section" ref={finalCtaRef}>
          <div className="cta-content">
            <h3>Ready to create your own?</h3>
            <p>
              These samples show just a glimpse of what our platform can do. Join the growing community of Australian teachers already transforming their workload with Gymea Edu.
            </p>
            <div className="final-cta-buttons">
              <a href="/" className="cta-button">Start a Free Trial</a>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}; 